package top.wcpe.allincore.ui.runtime.render.layer

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.RenderContext
import top.wcpe.allincore.ui.runtime.render.Renderer
import top.wcpe.allincore.ui.runtime.render.SlotItemView
import top.wcpe.allincore.ui.runtime.render.StaticItemNbt
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.model.RenderLayer
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess

/**
 * 槽位物品层渲染器：绘制物品图标和 quickCraft 预览。
 *
 * 与 [SlotBackgroundRenderer]、[SlotOverlayRenderer] 共同组成槽位分层渲染，对应 [RenderLayer.ITEM] 层。
 */
@Suppress("MagicNumber")
class SlotItemRenderer : Renderer {

    override fun layer(): RenderLayer = RenderLayer.ITEM

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        render(element, box, bridge, RenderContext.NONE)
    }

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge, context: RenderContext) {
        val slot = element as Slot
        val theme = ThemeAccess.current()

        val quickCraftAlpha = slot.quickCraftPreviewAlpha() ?: theme.slotQuickCraftPreviewAlpha

        // 解析物品数据（静态模式或动态模式）
        var item: SlotItemView? = null
        var preview: SlotItemView? = null
        val staticItem = slot.staticItem()
        if (staticItem != null) {
            val itemId = staticItem.itemId()
            if (!itemId.isNullOrEmpty()) {
                item = SlotItemView(itemId, staticItem.count(), StaticItemNbt.snbtOfCached(staticItem))
            }
        } else {
            item = context.slotItem(slot.groupId(), slot.key())
            preview = context.quickCraftPreview(slot.groupId(), slot.key())
        }

        // === 物品图标 ===
        if (item != null) {
            drawItem(slot, box, bridge, item)
        }

        // === quickCraft 预览 ===
        if (preview != null) {
            drawPreview(slot, box, bridge, preview, quickCraftAlpha)
        }
    }

    // ========== 物品图标 ==========

    private fun drawItem(slot: Slot, box: Box, bridge: UiRenderBridge, item: SlotItemView) {
        val geo = resolveGeometry(slot, box)
        val showCount = slot.showCount()
        // 改用携带数量锚点/偏移的规范重载：默认 countAnchor="bottom-right" + 零偏移
        // 复刻 vanilla 右下角数量徽标（含耐久条/冷却遮罩），行为不变。
        val offsetX = slot.countOffsetX() ?: 0f
        val offsetY = slot.countOffsetY() ?: 0f
        // 透传物品 SNBT，使平台层解析出自定义 bbmodel/贴图；透传槽位背景遮罩色，使物品随元素 opacity
        // 变暗时 3D 方块/贴图规则/bbmodel 物品也一致变暗（issue #68）。
        val backdropColor = SlotBackdropColor.resolve(slot, ThemeAccess.current())
        bridge.drawItemStack(
            item.itemId, item.nbt, item.count, false, showCount,
            slot.countAnchor(), offsetX, offsetY, backdropColor, geo.x, geo.y, geo.size, geo.size,
        )
    }

    // ========== quickCraft 预览 ==========

    private fun drawPreview(slot: Slot, box: Box, bridge: UiRenderBridge, preview: SlotItemView, alpha: Float) {
        val geo = resolveGeometry(slot, box)
        bridge.pushAlpha(alpha)
        try {
            // 预览物品（cursor 物品，可能是自定义物品）同样透传 SNBT。
            bridge.drawItemStack(
                preview.itemId, preview.nbt, preview.count, false, true,
                "bottom-right", 0f, 0f, geo.x, geo.y, geo.size, geo.size,
            )
        } finally {
            bridge.popAlpha()
        }
    }

    /** 物品绘制几何：图标边长 + 左上角绝对坐标（图标与预览共用，消除重复计算）。 */
    private fun resolveGeometry(slot: Slot, box: Box): ItemGeometry {
        val size = slot.itemSize() ?: (slot.slotSize() - DEFAULT_ITEM_BORDER)
        val insetX = slot.itemInsetX() ?: ((box.width() - size) / 2f)
        val insetY = slot.itemInsetY() ?: ((box.height() - size) / 2f)
        return ItemGeometry(size, box.x() + insetX, box.y() + insetY)
    }

    private data class ItemGeometry(val size: Float, val x: Float, val y: Float)

    private companion object {
        /** 槽位未显式指定 itemSize 时，图标相对槽位的默认内缩（每边 1px，共 2px）。 */
        private const val DEFAULT_ITEM_BORDER = 2f
    }
}
